<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    header="Select Image"
    modal
    class="image-picker-dialog"
    :style="{ width: '700px' }"
  >
    <!-- Upload area -->
    <div class="flex items-center gap-3 mb-4">
      <FileUpload
        mode="basic"
        accept="image/*"
        :max-file-size="5000000"
        choose-label="Upload Image"
        @select="handleUpload"
        :disabled="uploading"
      />
      <ProgressSpinner
        v-if="uploading"
        style="width: 24px; height: 24px"
        stroke-width="4"
      />
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <ProgressSpinner style="width: 40px; height: 40px" stroke-width="4" />
    </div>

    <!-- Empty state -->
    <div v-else-if="images.length === 0" class="text-center py-12 text-gray-400">
      <i class="pi pi-image text-5xl mb-3"></i>
      <p>No images uploaded yet</p>
    </div>

    <!-- Image grid -->
    <div v-else class="image-picker-grid">
      <div
        v-for="img in images"
        :key="img.filename"
        class="image-picker-item"
        :class="{ 'image-picker-item--selected': selectedUrl === img.url }"
        @click="selectedUrl = img.url"
      >
        <img :src="img.url" :alt="img.filename" />
        <div class="image-picker-item__overlay">
          <span class="image-picker-item__name" :title="img.filename">
            {{ img.filename }}
          </span>
          <button
            class="image-picker-item__delete"
            @click.stop="confirmDelete(img)"
            :title="'Delete ' + img.filename"
          >
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between w-full">
        <Button label="Cancel" class="p-button-text" @click="$emit('update:visible', false)" />
        <Button
          label="Select"
          :disabled="!selectedUrl"
          @click="confirmSelection"
        />
      </div>
    </template>

    <ConfirmDialog />
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import FileUpload from 'primevue/fileupload';
import ProgressSpinner from 'primevue/progressspinner';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import FilesService from '@/services/filesService';

export default {
  name: 'ImagePickerDialog',
  components: { Dialog, Button, FileUpload, ProgressSpinner, ConfirmDialog },
  props: {
    visible: { type: Boolean, default: false },
    modelValue: { type: String, default: '' },
  },
  emits: ['update:visible', 'update:modelValue'],
  setup() {
    const confirm = useConfirm();
    return { confirm };
  },
  data() {
    return {
      images: [],
      selectedUrl: '',
      loading: false,
      uploading: false,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.selectedUrl = this.modelValue || '';
        this.fetchImages();
      }
    },
  },
  methods: {
    async fetchImages() {
      this.loading = true;
      try {
        this.images = await FilesService.listContentImages();
      } catch (err) {
        console.error('Failed to load content images:', err);
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load images',
          life: 4000,
        });
      } finally {
        this.loading = false;
      }
    },

    async handleUpload(event) {
      const [file] = event.files || [];
      if (!file) return;

      this.uploading = true;
      try {
        const result = await FilesService.uploadContentImage(file, false);
        this.$toast?.add?.({
          severity: 'success',
          summary: 'Uploaded',
          detail: result.filename,
          life: 3000,
        });
        await this.fetchImages();
        this.selectedUrl = result.url;
      } catch (err) {
        if (err.response?.status === 409) {
          this.confirm.require({
            message: `"${file.name}" already exists. Overwrite it?`,
            header: 'File exists',
            acceptLabel: 'Overwrite',
            rejectLabel: 'Cancel',
            accept: () => this.uploadOverwrite(file),
          });
        } else {
          console.error('Upload failed:', err);
          this.$toast?.add?.({
            severity: 'error',
            summary: 'Upload failed',
            detail: err.response?.data?.message || err.message,
            life: 5000,
          });
        }
      } finally {
        this.uploading = false;
      }
    },

    async uploadOverwrite(file) {
      this.uploading = true;
      try {
        const result = await FilesService.uploadContentImage(file, true);
        this.$toast?.add?.({
          severity: 'success',
          summary: 'Replaced',
          detail: result.filename,
          life: 3000,
        });
        await this.fetchImages();
        this.selectedUrl = result.url;
      } catch (err) {
        console.error('Overwrite upload failed:', err);
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Upload failed',
          detail: err.response?.data?.message || err.message,
          life: 5000,
        });
      } finally {
        this.uploading = false;
      }
    },

    confirmDelete(img) {
      this.confirm.require({
        message: `Delete "${img.filename}"? This cannot be undone.`,
        header: 'Confirm Delete',
        acceptLabel: 'Delete',
        rejectLabel: 'Cancel',
        acceptClass: 'p-button-danger',
        accept: () => this.doDelete(img),
      });
    },

    async doDelete(img) {
      try {
        await FilesService.deleteContentImage(img.filename);
        if (this.selectedUrl === img.url) {
          this.selectedUrl = '';
        }
        await this.fetchImages();
        this.$toast?.add?.({
          severity: 'success',
          summary: 'Deleted',
          detail: img.filename,
          life: 3000,
        });
      } catch (err) {
        console.error('Delete failed:', err);
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Delete failed',
          detail: err.response?.data?.message || err.message,
          life: 5000,
        });
      }
    },

    confirmSelection() {
      this.$emit('update:modelValue', this.selectedUrl);
      this.$emit('update:visible', false);
    },
  },
};
</script>

<style scoped>
.image-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  max-height: 420px;
  overflow-y: auto;
  padding: 4px;
}

.image-picker-item {
  position: relative;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1;
  background: #f3f4f6;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.image-picker-item:hover {
  border-color: #93c5fd;
}

.image-picker-item--selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
}

.image-picker-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-picker-item__overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 20px 6px 6px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.image-picker-item__name {
  color: white;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 4px;
}

.image-picker-item__delete {
  background: none;
  border: none;
  color: #fca5a5;
  cursor: pointer;
  padding: 2px;
  font-size: 13px;
  flex-shrink: 0;
}

.image-picker-item__delete:hover {
  color: #ef4444;
}
</style>
